/** Fetch independent data together, but finish recovery before taking the
 * local-note snapshot. Journal deliveries created during recovery must be
 * included in the authoritative inbox validation and its final local pass. */
export async function refreshPrivateState<T>(steps: {
  /** Receives the head reading (when a head check ran) so the inbox step can
   * choose an incremental sync instead of the full download. */
  prepareInbox: (head?: unknown) => Promise<T>;
  recoverJournal?: () => Promise<boolean>;
  /** True means inbox sync already validated local notes against the chain. */
  syncInbox: (snapshot: T) => Promise<unknown>;
  resolveNotes: () => Promise<unknown>;
  /** Local validation after an unchanged head. It receives the head reading so
   * it can leave out notes an index committed at that head accounts for;
   * without it, resolveNotes runs. */
  resolveUnchanged?: (reading?: unknown) => Promise<unknown>;
  refreshHistory: () => Promise<unknown>;
  refreshPublic: () => Promise<unknown>;
  /** Prices, public balances and activity have their own loading/error state. */
  waitForAuxiliary?: boolean;
  onAuxiliaryError?: (error: unknown) => void;
  /** Journal recovery keeps retrying in the background; a failure there must
   * not block validating the notes the wallet already holds. */
  onRecoveryError?: (error: unknown) => void;
  /** Cheap server cursors: when they equal the last committed sync, nothing
   * is downloaded or re-validated. A failed read means a full refresh. */
  checkHead?: () => Promise<{
    unchanged: boolean;
    /** Nothing was ever delivered to this wallet and it holds no local note:
     * there is nothing to download or validate, index or not. */
    empty?: boolean;
    commit: () => Promise<void>;
    reading?: unknown;
  } | null>;
  onHeadResult?: (unchanged: boolean) => void;
  /** The head check failed (network, relayer, deadline): the refresh
   * validates in full and the caller can say why. */
  onHeadError?: (error: unknown) => void;
  onTiming?: (stage: string, durationMs: number, succeeded: boolean) => void;
  /** Journal recovery talks to the relayer. The balance refresh waits for it
   * at most this long; a later result is reported through onLateRecovery so
   * a slow relayer never holds the balance spinner. */
  recoveryWaitMs?: number;
  /** An unchanged head has nothing to validate, so it does not wait for
   * recovery at all unless told to. */
  unchangedRecoveryWaitMs?: number;
  onLateRecovery?: (complete: boolean) => void;
}): Promise<boolean> {
  const timed = async <R>(
    stage: string,
    work: () => Promise<R>,
  ): Promise<R> => {
    const started = performance.now();
    let succeeded = false;
    try {
      const result = await work();
      succeeded = true;
      return result;
    } finally {
      steps.onTiming?.(
        stage,
        Math.round(performance.now() - started),
        succeeded,
      );
    }
  };
  const balances = (async () => {
    const recoveryResult = timed(
      "recovery",
      steps.recoverJournal ?? (() => Promise.resolve(true)),
    ).catch((error: unknown) => {
      steps.onRecoveryError?.(error);
      return false;
    });
    let recoveryDone: { complete: boolean } | null = null;
    let recoveryLate = false;
    void recoveryResult.then((complete) => {
      recoveryDone = { complete };
      if (recoveryLate) steps.onLateRecovery?.(complete);
    });
    const waitForRecovery = (ms: number) =>
      new Promise<boolean>((resolve) => {
        if (recoveryDone) {
          resolve(recoveryDone.complete);
          return;
        }
        const timer = setTimeout(() => {
          recoveryLate = true;
          resolve(false);
        }, ms);
        void recoveryResult.then((complete) => {
          clearTimeout(timer);
          if (!recoveryLate) resolve(complete);
        });
      });
    const head = steps.checkHead
      ? await timed("head", steps.checkHead).catch((error: unknown) => {
          steps.onHeadError?.(error);
          return null;
        })
      : null;
    const nothingToSync = head?.unchanged === true || head?.empty === true;
    steps.onHeadResult?.(nothingToSync);
    if (head && nothingToSync) {
      // Journal recovery may still surface locally remembered outputs; when
      // it lands later, onLateRecovery indexes them without touching the
      // inbox or the chain snapshot.
      const complete = await waitForRecovery(
        steps.unchangedRecoveryWaitMs ?? 0,
      );
      // An empty wallet holds no note to check.
      if (!head.empty)
        await timed("local-validation", () =>
          steps.resolveUnchanged
            ? steps.resolveUnchanged(head.reading)
            : steps.resolveNotes(),
        );
      await head.commit().catch(() => undefined);
      return complete;
    }
    const [snapshot, complete] = await Promise.all([
      timed("inbox", () => steps.prepareInbox(head?.reading)),
      waitForRecovery(steps.recoveryWaitMs ?? 4_000),
    ]);
    const validated = await timed("validation", () =>
      steps.syncInbox(snapshot),
    );
    if (validated !== true) await timed("local-validation", steps.resolveNotes);
    if (head) await head.commit().catch(() => undefined);
    return complete;
  })();
  const auxiliary = Promise.all([
    timed("public-and-prices", steps.refreshPublic),
    timed("activity", steps.refreshHistory),
  ]);
  if (steps.waitForAuxiliary === false) {
    // Observe failures without keeping the private balance spinner alive.
    void auxiliary.catch((error) => steps.onAuxiliaryError?.(error));
    return balances;
  }
  const [recovered] = await Promise.all([balances, auxiliary]);
  return recovered;
}
